import React, { useState, useEffect } from "react";
import ContinueButton from "../../Registration/ContinueButton";
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";
import {
  submitProcurementDetails,
  saveProcurementInfoToStore,
} from "../../store/buyerFormDataSlice";

const ProcurementRoleForm = ({ onNext }) => {
  const dispatch = useDispatch();
  const procurementDetails = useSelector(
    (state) => state.buyerFormData.procurementInfo
  );
  const userId = useSelector((state) => state.auth.id);

  const [formData, setFormData] = useState({
    job_title: "",
    procurement_experience: "",
    specializations: [],
  });

  useEffect(() => {
    if (procurementDetails) {
      setFormData({
        job_title: procurementDetails.jobTitle || "",
        procurement_experience: procurementDetails.procurementExperience || "",
        specializations: procurementDetails.specializations || [],
      });
    }
  }, [procurementDetails]);

  const [errors, setErrors] = useState({});

  useEffect(() => {
    const validateForm = () => {
      const newErrors = {};

      if (
        !formData.job_title ||
        typeof formData.job_title !== "string" ||
        formData.job_title.trim() === ""
      ) {
        newErrors.job_title = "This field is required.";
      }

      if (
        formData.procurement_experience === null ||
        formData.procurement_experience === undefined ||
        formData.procurement_experience === ""
      ) {
        newErrors.procurement_experience = "This field is required.";
      }
    };

    validateForm();
  }, [formData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (selectedOptions) => {
    setFormData((prev) => ({
      ...prev,
      specializations: selectedOptions
        ? selectedOptions.map((opt) => opt.value)
        : [],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const hasChanged = Object.keys(formData).some(
      (key) => formData[key] !== procurementDetails?.[key]
    );

    if (!hasChanged) {
      onNext();
      return;
    }

    const procurementInfo = {
      createdBy: userId,
      jobTitle: formData.job_title,
      procurementExperience: formData.procurement_experience,
      specializations: formData.specializations,
    };

    await dispatch(submitProcurementDetails(procurementInfo));
    dispatch(saveProcurementInfoToStore(procurementInfo));
    onNext();
  };

  const isDisabled = !formData.job_title || !formData.procurement_experience;

  const customMultiSelectStyles = {
    menu: (provided) => ({
      ...provided,
      textAlign: "start",
      background: "#F7F7F7",
    }),
    control: (provided, state) => ({
      ...provided,
      ...provided,
      border: "none",
      background: "#F9F9F9",
      textAlign: "start",
      minHeight: "48px",
      height: "auto",
      boxShadow: state.isFocused
        ? "0 0 0 2px rgba(128, 128, 128, 0.5)"
        : provided.boxShadow,
      overflowY: "hidden",
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "#1D2639",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "lightgray"
        : state.isFocused
        ? "lightgray"
        : provided.backgroundColor,
      color: state.isSelected ? "black" : "black",
      padding: "6px",
      "&:active": {
        backgroundColor: "lightgray",
      },
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#B0B0B0",
    }),
  };

  const specializationsOptions = [
    "Chief Procurement Officer (CPO)",
    "Procurement Manager",
    "Strategic Sourcing Manager",
    "Procurement Analyst",
    "Procurement Specialist",
    "Buyer/Purchasing Agent",
    "Category Manager",
    "Contract Manager",
    "Supplier Relationship Manager",
    "Logistics Manager",
  ];

  return (
    <section className="flex flex-col min-h-screen justify-start items-center py-10 px-6">
      <header className="mb-14">
      <h3 className="text-xl font-medium text-[#7E7E7E] text-center">Step 3</h3>
        <h1 className="text-2xl font-semibold text-[#1D2639] text-center">
          Your Procurement Role
        </h1>
        <p className="text-base font-medium text-[#7E7E7E] text-center mt-2">
          By detailing your job title, procurement experience, and
          specializations, we can better support your professional objectives
          and provide resources that align with your expertise and focus areas.
        </p>
      </header>
      <form
        className="flex flex-col w-full sm:w-[572px]"
        onSubmit={handleSubmit}
      >
        {[
          {
            label: "Job Title",
            name: "job_title",
            type: "text",
            required: true,
          },
          {
            label: "Procurement Experience (years)",
            name: "procurement_experience",
            type: "number",
            min: 0,
            max: 50,
            required: true,
          },
          {
            label: "Specializations",
            name: "specializations",
            type: "select",
            options: specializationsOptions,
            multiple: true,
          },
        ].map(
          ({ label, name, type, options, required, min, max, multiple }) => (
            <fieldset key={name} className="mb-8">
              <label
                className="block text-[#1D2639] text-[12px] text-start font-normal"
                htmlFor={name}
              >
                {label} {required && <span className="text-red-500">*</span>}
              </label>
              {type === "select" ? (
                <Select
                  id={name}
                  name={name}
                  options={options.map((option) => ({
                    label: option,
                    value: option,
                  }))}
                  value={formData.specializations?.map((val) => ({
                    label: val,
                    value: val,
                  }))}
                  onChange={handleSelectChange}
                  isMulti={true}
                  placeholder="Select your specializations"
                  styles={customMultiSelectStyles}
                  className="w-full"
                />
              ) : (
                <input
                  type={type}
                  id={name}
                  name={name}
                  placeholder={`Enter ${label}`}
                  min={min}
                  max={max}
                  className={`w-full h-12 p-3 bg-[#F9F9F9] rounded-lg border-2 border-gray-300 ${
                    errors[name] ? "border-red-500" : ""
                  }`}
                  value={formData[name]}
                  onChange={handleChange}
                  required={required}
                />
              )}
            </fieldset>
          )
        )}
        <ContinueButton
          onClick={handleSubmit}
          label="Save and Continue"
          isDisabled={isDisabled}
        />
      </form>
    </section>
  );
};

export default ProcurementRoleForm;
